<template>
    <div class="module">
        <div class="slide-wrapper">
            <div class="slide-transition-wrapper">
                <transition name="fade" appear>
                    <div v-if="moduleListVisible" class="module-list-wrapper">
                        <ModuleList />
                    </div>
                </transition>
                <transition
                    name="fade"
                    @before-leave="beforeLeaveSlide"
                    @after-leave="afterLeaveSlide"
                >
                    <component
                        :is="slideComponent"
                        ref="slideComponent"
                        class="slide"
                    />
                </transition>
            </div>
            <ControlButton
                :active="prevButtonActive"
                :direction="-1"
                :slide-handler="slidePrevHandler"
                :class="['prev', prevButtonTutorial && 'tutorial']"
            >
                {{ prevButtonLabel }}
            </ControlButton>
            <ControlButton
                :active="extraButtonActive"
                :slide-handler="extraButtonHandler"
                :class="['extra', extraButtonLeft && 'left', extraButtonTutorial && 'tutorial']"
            >
                {{ extraButtonLabel }}
            </ControlButton>
            <ControlButton
                :active="nextButtonActive"
                :direction="1"
                :slide-handler="slideNextHandler"
                :class="['next', nextButtonTutorial && 'tutorial', nextButtonGreen && 'green']"
            >
                {{ nextButtonLabel }}
            </ControlButton>
        </div>
    </div>
</template>

<script>
import guardModuleRoute from '@/service/guardModuleRoute';
import modules from '../static/modules.json';

const modulesConfig = [];
for (const key in modules)
{
    const moduleSplit = key.split('.').map(number => parseInt(number, 10));
    if (moduleSplit.length < 2) { continue; }
    const [moduleNumber, unit] = moduleSplit;
    if (modulesConfig[moduleNumber] === undefined)
    {
        modulesConfig[moduleNumber] = [];
    }
    modulesConfig[moduleNumber].push({
        key,
        moduleNumber,
        unit
    });
}

export default {
    name: 'Module',
    beforeRouteEnter(to, from, next)
    {
        guardModuleRoute(to, from, next);
    },
    beforeRouteUpdate(to, from, next)
    {
        guardModuleRoute(to, from, next);
        if (to.params.module === from.params.module)
        {
            this.previousRoute = from;
        }
    },
    props: {
        module: {
            type: String,
            required: true
        },
        slide: {
            type: Number,
            default: 1
        }
    },
    data: () => ({
        slideComponent: null,
        prevButtonActive: false,
        nextButtonActive: false,
        extraButtonActive: false,
        extraButtonLeft: false,
        prevButtonTutorial: false,
        nextButtonTutorial: false,
        extraButtonTutorial: false,
        nextButtonGreen: false,
        prevButtonLabel: 'Zurück',
        nextButtonLabel: 'Weiter',
        extraButtonLabel: '',
        slidePrevHandler: null,
        slideNextHandler: null,
        extraButtonHandler: null,
        lastTransitionDirection: 0,
        slideCount: 0,
        previousRoute: null,
        moduleListVisible: false
    }),
    computed: {
        config()
        {
            return modules[this.module];
        }
    },
    watch: {
        slide(newSlide)
        {
            this.gotoSlide(newSlide);
        },
        $route(to, from)
        {
            this.lastTransitionDirection = parseInt(to.params.slide || 1, 10) -
                parseInt(from.params.slide || 1, 10);
        }
    },
    created()
    {
        this._leaveInProgress = false;
        this._heightChangeScheduled = false;
        // eslint-disable-next-line prefer-template
        this._module = import('@/components/slides/' + this.module + '/config');
        this._module.then((module) =>
        {
            this.slideCount = module.default.slides.length;
            this.initHeader();
        });

        this.gotoSlide(this.slide);
    },
    methods:
    {
        gotoSlide(number)
        {
            this._module.then((module) =>
            {
                const {slides} = module.default;
                this.slideComponent = slides[number - 1];
            });
            this.$root.header.progress.currentSlide = number;

            const {config} = this;
            let {subtitle} = this.config;

            if (config.overwriteSubtitle && config.overwriteSubtitle[number])
            {
                subtitle = config.overwriteSubtitle[number];
            }

            this.$root.header.subtitle = subtitle;
        },
        animateTransition(config)
        {
            config.triggerHandler = this.triggerContent;
            this.$root.animateTransition(config);
        },
        triggerContent(index)
        {
            const {slideComponent} = this.$refs;
            if (slideComponent)
            {
                slideComponent[`content${index}Visible`] = true;
                if (slideComponent.changeHeightOnTrigger === index)
                {
                    if (!this._leaveInProgress)
                    {
                        this.$nextTick(this.$root.executeHeightChange);
                    }
                    else
                    {
                        this._heightChangeScheduled = true;
                    }
                }
            }
        },
        initHeader()
        {
            const {config, slide, $root, $route} = this;
            const title = config.mainTitle;
            const progress = $route.name === 'module';

            let {subtitle} = config;

            if (config.overwriteSubtitle && config.overwriteSubtitle[slide])
            {
                subtitle = config.overwriteSubtitle[slide];
            }

            $root.resetHeader(subtitle, title, progress);

            if ($route.name === 'tutorial')
            {
                $root.header.onlyHome = true;
            }

            if (!progress) { return; }

            const [moduleIndex] = this.module
                .split('.')
                .map(number => parseInt(number, 10));

            $root.header.progress.modules = modulesConfig[moduleIndex];
            $root.header.progress.slideCount = this.slideCount;
        },
        beforeLeaveSlide()
        {
            this.$root.scrollToTop();
            this.$root.prepareHeightChange();
            this._leaveInProgress = true;
        },
        afterLeaveSlide()
        {
            this._leaveInProgress = false;
            if (this._heightChangeScheduled)
            {
                this.$root.executeHeightChange();
                this._heightChangeScheduled = false;
            }
        }
    }
};
</script>

<style lang="scss" src="./Module.scss" />
